import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CircleHelp, Loader2, MoreVertical, ZoomIn } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import ReuploadCommentDialog from './ReuploadCommentDialog';
import type { AdminOrderExportDpi, AdminOrderItem, AdminOrderItemStatus } from '@/services/adminService';
import { analytics } from '@/services/analytics';
import { translateMeasurementUnitLabel } from '@/types/measurement';
import type { MeasurementUnit } from '@/types/measurement';
import { ORDER_ITEM_STATUSES } from '@/types/orderStatus';
import {
  PAID_UI_ITEM_STATUS,
  getItemStatusLabel,
  isPaidUiItemStatus,
  isReadonlyItemStatusOption,
  type AdminOrderItemUiStatus,
  type AdminWorkspaceCurrencyCode,
  type AdminWorkspaceExportDpi,
  AdminWorkspaceItemGroup,
} from './types';
import {
  appendUnitLabel,
  formatDimensions,
  formatLowPpiBadge,
  formatLowPpiTitle,
  formatMeasurement,
  formatOriginalPhysicalSize,
  formatPixelSize,
  getFullPrintableSizeInches,
  getVisibleArtSizeInches,
  formatPrice,
  formatPrintPpi,
  formatAdminDateTime,
  formatOrderItemReadableCode,
} from './utils';
import { formatPpi } from '@/components/order/utils/printSizeForm';

type PendingAction = {
  title: string;
  description: ReactNode;
  confirmText: string;
  onConfirm: () => void;
};

interface ItemGroupsSectionProps {
  groups: AdminWorkspaceItemGroup[];
  status: 'idle' | 'loading' | 'error';
  measurementUnit: MeasurementUnit;
  currencyCode: AdminWorkspaceCurrencyCode;
  usdToUahRate: number | null;
  updatingIds: Set<string>;
  exportingGroupKeys: Set<string>;
  exportDpiByGroupKey: Record<string, AdminWorkspaceExportDpi>;
  downloadingWithMarginIds: Set<string>;
  onExportGroup: (groupKey: string, items: AdminOrderItem[], targetDpi: AdminOrderExportDpi) => void;
  onExportDpiChange: (groupKey: string, targetDpi: AdminOrderExportDpi) => void;
  onItemStatusChange: (orderItemId: string, status: AdminOrderItemStatus) => void;
  onDownloadItemWithMargin: (item: AdminOrderItem) => void;
  onOpenOrder: (displayCode: string) => void;
  reuploadDialogItem: AdminOrderItem | null;
  reuploadComment: string;
  reuploadSubmitting: boolean;
  onReuploadCommentChange: (value: string) => void;
  onReuploadDialogConfirm: () => void;
  onReuploadDialogOpenChange: (open: boolean) => void;
  onOpenPreview: (url: string | null, meta?: {
    widthIn: number | null;
    heightIn: number | null;
    marginIn: number | null;
    galleryWrapIn: number | null;
    originalWidthPx: number | null;
    originalHeightPx: number | null;
  } | null) => void;
}

const EXPORTABLE_ITEM_STATUSES: ReadonlySet<AdminOrderItemStatus> = new Set([
  'FIRST_ORDER',
  'SECOND_ORDER',
  'PAID',
  'REUPLOAD_IMAGE',
  'REUPLOADED',
  'READY_TO_PRINT',
]);

const isEligibleForExport = (item: AdminOrderItem): boolean => EXPORTABLE_ITEM_STATUSES.has(item.status);

const isForcedDpiMode = (mode: AdminOrderExportDpi): boolean => mode === '300' || mode === '600' || mode === '1200';

const hasEdgeTreatment = (item: AdminOrderItem): boolean => (item.marginIn ?? 0) > 0 || (item.galleryWrapIn ?? 0) > 0;

const buildGroupExportOutputDescription = (
  t: ReturnType<typeof useTranslation>['t'],
  targetDpi: AdminOrderExportDpi,
  items: AdminOrderItem[],
): string => {
  if (targetDpi === 'ORIGINAL') {
    return t(
      'adminOrdersWorkspace.confirmations.exportOutputOriginal',
      'Output: original uploaded files. Margin, gallery wrap, and size changes are ignored.',
    );
  }

  if (targetDpi === 'RIP') {
    const hasMetadataOnlyCandidates = items.some((item) => !hasEdgeTreatment(item));
    return hasMetadataOnlyCandidates
      ? t(
          'adminOrdersWorkspace.confirmations.exportOutputRipMixed',
          'Output: ZIP archive of per-file print exports. Files without margin or gallery wrap may keep the original JPEG, PNG, or TIFF format with updated DPI metadata; files that need processing are exported as TIFF.',
        )
      : t(
          'adminOrdersWorkspace.confirmations.exportOutputRipTiff',
          'Output: ZIP archive of per-file TIFF print files.',
        );
  }

  return t(
    'adminOrdersWorkspace.confirmations.exportOutputZip',
    'Output: ZIP archive with TIFF print files.',
  );
};

const buildSingleFileReadyOutputDescription = (
  t: ReturnType<typeof useTranslation>['t'],
  targetDpi: AdminOrderExportDpi,
  item: AdminOrderItem,
): string => {
  if (targetDpi === 'ORIGINAL') {
    return t(
      'adminOrdersWorkspace.confirmations.exportOutputOriginal',
      'Output: original uploaded file. Margin, gallery wrap, and size changes are ignored.',
    );
  }

  if (isForcedDpiMode(targetDpi)) {
    return t(
      'adminOrdersWorkspace.confirmations.exportOutputTiff',
      'Output: TIFF print file.',
    );
  }

  if (!hasEdgeTreatment(item)) {
    return t(
      'adminOrdersWorkspace.confirmations.exportOutputMetadataOnlyWhenPossible',
      'Output: if no resize is needed, the original JPEG, PNG, or TIFF file is kept and only DPI metadata is updated; otherwise a TIFF print file is created.',
    );
  }

  return t(
    'adminOrdersWorkspace.confirmations.exportOutputTiff',
    'Output: TIFF print file.',
  );
};

const ItemActionMenu = () => {
  const { t } = useTranslation();

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      disabled
      aria-label={t('adminOrdersWorkspace.table.noActions', 'No actions available')}
    >
      <MoreVertical className="size-4" />
      <span className="sr-only">{t('adminOrdersWorkspace.table.noActions', 'No actions available')}</span>
    </Button>
  );
};

const ItemGroupsSection = ({
  groups,
  status,
  measurementUnit,
  currencyCode,
  usdToUahRate,
  updatingIds,
  exportingGroupKeys,
  downloadingWithMarginIds,
  exportDpiByGroupKey,
  onExportGroup,
  onExportDpiChange,
  onItemStatusChange,
  onDownloadItemWithMargin,
  onOpenOrder,
  reuploadDialogItem,
  reuploadComment,
  reuploadSubmitting,
  onReuploadCommentChange,
  onReuploadDialogConfirm,
  onReuploadDialogOpenChange,
  onOpenPreview,
}: ItemGroupsSectionProps) => {
  const { t } = useTranslation();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const unitLabel = useMemo(() => translateMeasurementUnitLabel(t, measurementUnit), [measurementUnit, t]);
  const exportModeDescriptions = useMemo<ReadonlyArray<{
    value: AdminOrderExportDpi;
    label: string;
    description: string;
  }>>(() => [
    {
      value: 'ORIGINAL',
      label: t('adminOrdersWorkspace.exportModes.original.label', 'Original'),
      description: t(
        'adminOrdersWorkspace.exportModes.original.description',
        'Exports the original uploaded files. Margin, gallery wrap, and size changes are ignored.',
      ),
    },
    {
      value: 'RIP',
      label: t('adminOrdersWorkspace.exportModes.rip.label', 'RIP'),
      description: t(
        'adminOrdersWorkspace.exportModes.rip.description',
        'Uses the single-file ready-for-print logic for each item separately, without aligning the whole group to one common resolution.',
      ),
    },
    {
      value: 'AUTO',
      label: t('adminOrdersWorkspace.exportModes.auto.label', 'MAX PPI'),
      description: t(
        'adminOrdersWorkspace.exportModes.auto.description',
        'Uses one shared resolution for the whole group based on the highest effective print PPI in that group.',
      ),
    },
    {
      value: '300',
      label: t('adminOrdersWorkspace.exportModes.300.label', '300 PPI'),
      description: t(
        'adminOrdersWorkspace.exportModes.300.description',
        'Forces all files in the group to one shared 300 PPI print-ready resolution.',
      ),
    },
    {
      value: '600',
      label: t('adminOrdersWorkspace.exportModes.600.label', '600 PPI'),
      description: t(
        'adminOrdersWorkspace.exportModes.600.description',
        'Forces all files in the group to one shared 600 PPI print-ready resolution.',
      ),
    },
    {
      value: '1200',
      label: t('adminOrdersWorkspace.exportModes.1200.label', '1200 PPI'),
      description: t(
        'adminOrdersWorkspace.exportModes.1200.description',
        'Forces all files in the group to one shared 1200 PPI print-ready resolution.',
      ),
    },
  ], [t]);
  const exportModeDescriptionMap = useMemo(
    () => new Map(exportModeDescriptions.map((mode) => [mode.value, mode])),
    [exportModeDescriptions],
  );

  const closePendingAction = () => setPendingAction(null);
  const confirmPendingAction = () => {
    const action = pendingAction;
    setPendingAction(null);
    action?.onConfirm();
  };

  if (groups.length === 0 && status !== 'loading') {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {t('adminOrdersWorkspace.table.empty', 'No paid order items match the selected filters.')}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {groups.map((group) => {
        const selectedGroupTargetDpi = exportDpiByGroupKey[group.key] ?? 'AUTO';
        const selectedGroupMode = exportModeDescriptionMap.get(selectedGroupTargetDpi);
        const eligibleGroupItems = group.items.filter(isEligibleForExport);

        return (
        <Card key={group.key}>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle>{group.paper}</CardTitle>
                <CardDescription>{group.printer}</CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('adminOrdersWorkspace.exportModes.helpLabel', 'Export mode help')}>
                        <CircleHelp className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm">
                      <div className="space-y-2 text-xs">
                        {exportModeDescriptions.map((mode) => (
                          <div key={mode.value}>
                            <p className="font-semibold text-foreground">{mode.label}</p>
                            <p className="text-muted-foreground">{mode.description}</p>
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Select
                  value={selectedGroupTargetDpi}
                  onValueChange={(value) => onExportDpiChange(group.key, value as AdminOrderExportDpi)}
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ORIGINAL">{t('adminOrdersWorkspace.exportModes.original.label', 'Original')}</SelectItem>
                    <SelectItem value="RIP">{t('adminOrdersWorkspace.exportModes.rip.label', 'RIP')}</SelectItem>
                    <SelectItem value="AUTO">{t('adminOrdersWorkspace.exportModes.auto.label', 'MAX PPI')}</SelectItem>
                    <SelectItem value="300">{t('adminOrdersWorkspace.exportModes.300.label', '300 PPI')}</SelectItem>
                    <SelectItem value="600">{t('adminOrdersWorkspace.exportModes.600.label', '600 PPI')}</SelectItem>
                    <SelectItem value="1200">{t('adminOrdersWorkspace.exportModes.1200.label', '1200 PPI')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPendingAction({
                      title: t('adminOrdersWorkspace.confirmations.exportGroupTitle', 'Confirm group export'),
                      description: (
                        <div className="space-y-2">
                          <p>{group.paper} · {group.printer}</p>
                          <p>{t('adminOrdersWorkspace.confirmations.filesIncluded', 'Files included')}: {eligibleGroupItems.length} / {group.items.length}</p>
                          <p>{t('adminOrdersWorkspace.confirmations.exportMode', 'Export mode')}: {selectedGroupMode?.label ?? selectedGroupTargetDpi}</p>
                          <p>{selectedGroupMode?.description}</p>
                          <p>{buildGroupExportOutputDescription(t, selectedGroupTargetDpi, eligibleGroupItems)}</p>
                        </div>
                      ),
                      confirmText: t('adminOrdersWorkspace.confirmations.exportButton', 'Export'),
                      onConfirm: () => onExportGroup(group.key, group.items, selectedGroupTargetDpi),
                    });
                  }}
                  disabled={exportingGroupKeys.has(group.key) || group.items.length === 0}
                >
                {exportingGroupKeys.has(group.key) ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('adminOrdersWorkspace.actions.exporting', 'Exporting')}
                  </span>
                ) : t('adminOrdersWorkspace.actions.exportGroup', 'Export group')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminOrdersWorkspace.table.preview', 'Preview')}</TableHead>
                  <TableHead>{t('adminOrdersWorkspace.table.customer', 'Customer')}</TableHead>
                  <TableHead>{t('adminOrdersWorkspace.table.orderItem', 'Order / Item')}</TableHead>
                  <TableHead>{t('adminOrdersWorkspace.table.printSize', 'Print size')}</TableHead>
                  <TableHead>{t('adminOrdersWorkspace.table.originalFileSize', 'Original file size')}</TableHead>
                  <TableHead>{t('adminOrdersWorkspace.table.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('adminOrdersWorkspace.table.price', 'Price')}</TableHead>
                  <TableHead className="text-right">{t('adminOrdersWorkspace.table.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.items.map((item) => {
                  const lowPpiBadge = formatLowPpiBadge(item);
                  const lowPpiTitle = formatLowPpiTitle(item);
                  const customerName = item.customer?.name ?? item.shippingAddress?.firstName ?? null;
                  const customerLabel = customerName
                    ? `${customerName}${item.shippingAddress?.lastName ? ` ${item.shippingAddress.lastName}` : ''}`
                    : item.customer?.email ?? t('adminOrdersWorkspace.common.unknownCustomer', 'Unknown customer');
                  const addressLine = item.shippingAddress
                    ? [item.shippingAddress.city, item.shippingAddress.region, item.shippingAddress.countryCode].filter(Boolean).join(', ')
                    : null;

                  const selectedTargetDpi = exportDpiByGroupKey[group.key] ?? 'AUTO';
                  const downloadMode = exportModeDescriptionMap.get(selectedTargetDpi);
                  const fullPrintableSize = getFullPrintableSizeInches(item);
                  const visibleArtSize = getVisibleArtSizeInches(item);
                  const quantity = typeof item.quantity === 'number' && Number.isFinite(item.quantity)
                    ? Math.max(1, Math.min(99, Math.round(item.quantity)))
                    : 1;
                  const hasMultipleQuantity = quantity > 1;

                  const statusSelectValue: AdminOrderItemUiStatus = isPaidUiItemStatus(item.status) ? PAID_UI_ITEM_STATUS : item.status;

                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex flex-col items-center gap-2">
                          <div className="group relative flex h-16 w-24 items-center justify-center overflow-hidden rounded-md bg-muted/40">
                            {item.uploadedFile?.thumbnailSignedUrl ? (
                              <img
                                src={item.uploadedFile.thumbnailSignedUrl}
                                alt={item.uploadedFile.filename}
                                className="max-h-full max-w-full object-contain"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center rounded-md border border-dashed border-muted-foreground/40 bg-muted text-[11px] text-muted-foreground">
                                {t('adminOrdersWorkspace.table.noPreview', 'No preview')}
                              </div>
                            )}
                            {lowPpiBadge ? (
                              <span
                                className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-800 shadow-sm"
                                title={lowPpiTitle}
                                aria-label={lowPpiTitle}
                              >
                                <AlertTriangle className="h-3 w-3" />
                              </span>
                            ) : null}
                            {item.uploadedFile?.signedUrl ? (
                              <button
                                type="button"
                                onClick={() => {
                                  analytics.track('button: ui.admin_print_orders_preview_zoom', {
                                    orderItemId: item.id,
                                    fileId: item.uploadedFile?.id ?? null,
                                  });
                                  onOpenPreview(item.uploadedFile?.signedUrl ?? null, {
                                    widthIn: item.widthIn ?? null,
                                    heightIn: item.heightIn ?? null,
                                    marginIn: item.marginIn ?? null,
                                    galleryWrapIn: item.galleryWrapIn ?? null,
                                    originalWidthPx: item.uploadedFile?.originalWidthPx ?? null,
                                    originalHeightPx: item.uploadedFile?.originalHeightPx ?? null,
                                  });
                                }}
                                className="absolute inset-x-1 bottom-1 inline-flex items-center justify-center gap-1 rounded bg-black/70 px-2 py-0.5 text-[11px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100"
                                aria-label={t('adminOrdersWorkspace.table.zoomLabel', 'Zoom')}
                              >
                                <ZoomIn className="h-3 w-3" />
                                {t('adminOrdersWorkspace.table.zoom', 'Zoom')}
                              </button>
                            ) : null}
                          </div>
                          {item.uploadedFile?.downloadUrl || item.uploadedFile?.signedUrl ? (
                            <div className="flex flex-col items-center gap-1">
                              <button
                                type="button"
                                className="text-[11px] font-semibold text-blue-600 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                                onClick={() => {
                                  setPendingAction({
                                    title: t('adminOrdersWorkspace.confirmations.downloadReadyTitle', 'Confirm file download'),
                                    description: (
                                      <div className="space-y-2">
                                        <p>{item.uploadedFile?.filename ?? '—'}</p>
                                        <p>{t('adminOrdersWorkspace.table.printSize', 'Print size')}: {appendUnitLabel(formatDimensions(fullPrintableSize.width, fullPrintableSize.height, measurementUnit), unitLabel)}</p>
                                        <p>{t('adminOrdersWorkspace.table.visibleArtSize', 'Visible art size')}: {appendUnitLabel(formatDimensions(visibleArtSize.width, visibleArtSize.height, measurementUnit), unitLabel)}</p>
                                        <p>{selectedTargetDpi === 'ORIGINAL'
                                          ? `${t('adminOrdersWorkspace.table.originalFileSize', 'Original file size')}: ${formatPixelSize(item.uploadedFile?.originalWidthPx ?? null, item.uploadedFile?.originalHeightPx ?? null)}`
                                          : `${t('adminOrdersWorkspace.table.resultPpi', 'Result PPI')}: ${selectedTargetDpi === '300' || selectedTargetDpi === '600' || selectedTargetDpi === '1200' ? `${selectedTargetDpi} PPI` : formatPrintPpi(item)}`}</p>
                                        <p>{selectedTargetDpi === 'ORIGINAL'
                                          ? `${t('adminOrdersWorkspace.table.filePpi', 'File PPI')}: ${item.uploadedFile ? formatPpi(item.uploadedFile.dpiX, item.uploadedFile.dpiY) : '—'}`
                                          : `${t('orderSummary.marginValue', 'Margin')}: ${appendUnitLabel(formatMeasurement(item.marginIn, measurementUnit), unitLabel)}`}</p>
                                        {selectedTargetDpi === 'ORIGINAL' ? null : (
                                          <p>{t('orderSummary.galleryWrapValue', 'Gallery Wrap')}: {appendUnitLabel(formatMeasurement(item.galleryWrapIn, measurementUnit), unitLabel)}</p>
                                        )}
                                        <p>{t('adminOrdersWorkspace.confirmations.exportMode', 'Export mode')}: {downloadMode?.label ?? selectedTargetDpi}</p>
                                        <p>{downloadMode?.description}</p>
                                        <p>{buildSingleFileReadyOutputDescription(t, selectedTargetDpi, item)}</p>
                                      </div>
                                    ),
                                    confirmText: t('adminOrdersWorkspace.confirmations.downloadButton', 'Download'),
                                    onConfirm: () => onDownloadItemWithMargin(item),
                                  });
                                }}
                                disabled={downloadingWithMarginIds.has(item.id)}
                              >
                                {downloadingWithMarginIds.has(item.id)
                                  ? t('adminOrdersWorkspace.table.preparingPrintFile', 'Preparing file')
                                  : t('adminOrdersWorkspace.table.downloadFile', 'Download file')}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{customerLabel}</p>
                          {item.customer?.email ? <p className="text-xs text-muted-foreground">{item.customer.email}</p> : null}
                          {addressLine ? <p className="text-xs text-muted-foreground">{addressLine}</p> : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p>{t('adminOrdersWorkspace.table.order', 'Order')}: {item.order?.displayCode ?? t('adminOrdersWorkspace.table.noOrder', 'no order')}</p>
                          <p title={item.id}>{t('adminOrdersWorkspace.table.itemId', 'Item id')}: {formatOrderItemReadableCode(item.id)}</p>
                          <p>
                            {t('adminOrdersWorkspace.table.quantity', 'Qty')}: {' '}
                            <span
                              className={hasMultipleQuantity
                                ? 'inline-flex min-w-8 items-center justify-center rounded-md bg-amber-100 px-2 py-0.5 font-semibold text-amber-900 ring-1 ring-amber-300'
                                : 'font-medium text-foreground'}
                            >
                              {quantity}
                            </span>
                          </p>
                          <p>{t('adminOrdersWorkspace.table.updated', 'Updated')}: {formatAdminDateTime(item.updatedAt)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p>{t('adminOrdersWorkspace.table.printSize', 'Print size')}: {appendUnitLabel(formatDimensions(fullPrintableSize.width, fullPrintableSize.height, measurementUnit), unitLabel)}</p>
                          <p>{t('adminOrdersWorkspace.table.visibleArtSize', 'Visible art size')}: {appendUnitLabel(formatDimensions(visibleArtSize.width, visibleArtSize.height, measurementUnit), unitLabel)}</p>
                          <p>{t('adminOrdersWorkspace.table.resultPpi', 'Result PPI')}: {formatPrintPpi(item)}</p>
                          <p>{t('orderSummary.marginValue', 'Margin')}: {appendUnitLabel(formatMeasurement(item.marginIn, measurementUnit), unitLabel)}</p>
                          <p>{t('orderSummary.galleryWrapValue', 'Gallery Wrap')}: {appendUnitLabel(formatMeasurement(item.galleryWrapIn, measurementUnit), unitLabel)}</p>
                          {lowPpiBadge ? (
                            <Badge
                              variant="outline"
                              className="inline-flex w-fit items-center gap-1 border-amber-300 bg-amber-50 text-amber-800"
                              title={lowPpiTitle}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {lowPpiBadge}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p className="max-w-[220px] truncate text-sm font-medium text-foreground" title={item.uploadedFile?.filename ?? undefined}>{item.uploadedFile?.filename ?? '—'}</p>
                          <p>{formatPixelSize(item.uploadedFile?.originalWidthPx ?? null, item.uploadedFile?.originalHeightPx ?? null)}</p>
                          <p>{t('adminOrdersWorkspace.table.originalSize', 'Original size')}: {appendUnitLabel(formatOriginalPhysicalSize(item, measurementUnit) ?? '—', unitLabel)}</p>
                          <p>{t('adminOrdersWorkspace.table.filePpi', 'File PPI')}: {item.uploadedFile ? formatPpi(item.uploadedFile.dpiX, item.uploadedFile.dpiY) : '—'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <Select
                            key={`${item.id}:${item.status}:${updatingIds.has(item.id) ? 'saving' : 'idle'}`}
                            value={statusSelectValue}
                            onValueChange={(value) => {
                              if (value === PAID_UI_ITEM_STATUS) {
                                return;
                              }

                              analytics.track('dropdown: ui.admin_print_orders_status_change', {
                                orderItemId: item.id,
                                fromStatus: item.status,
                                toStatus: value,
                              });
                              onItemStatusChange(item.id, value as AdminOrderItemStatus);
                            }}
                            disabled={updatingIds.has(item.id) || !item.orderId || item.status === 'NEW' || item.status === 'IN_ORDER'}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ORDER_ITEM_STATUSES.map((statusOption) => {
                                if (statusOption === 'FIRST_ORDER' || statusOption === 'SECOND_ORDER') {
                                  return null;
                                }

                                if (statusOption === 'PAID') {
                                  return (
                                    <SelectItem
                                      key={PAID_UI_ITEM_STATUS}
                                      value={PAID_UI_ITEM_STATUS}
                                      disabled
                                    >
                                      {getItemStatusLabel(t, 'PAID')}
                                    </SelectItem>
                                  );
                                }

                                return (
                                  <SelectItem
                                    key={statusOption}
                                    value={statusOption}
                                    disabled={isReadonlyItemStatusOption(item.status, statusOption)}
                                  >
                                    {getItemStatusLabel(t, statusOption)}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {item.status === 'FIRST_ORDER' || item.status === 'SECOND_ORDER' ? (
                            <Badge className="w-fit bg-amber-100 text-amber-900 hover:bg-amber-100">
                              {getItemStatusLabel(t, item.status)}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasMultipleQuantity ? (
                          <div className="space-y-1 text-right">
                            <p className="text-sm font-medium text-foreground">
                              {t('adminOrdersWorkspace.table.unitPrice', 'Unit')}: {formatPrice(item.priceUsd, currencyCode, usdToUahRate)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t('adminOrdersWorkspace.table.totalPrice', 'Total')}: {formatPrice((item.priceUsd ?? 0) * quantity, currencyCode, usdToUahRate)}
                            </p>
                          </div>
                        ) : formatPrice(item.priceUsd, currencyCode, usdToUahRate)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-col items-end gap-2">
                          <ItemActionMenu />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenOrder(item.order?.displayCode ?? '')}
                            disabled={!item.order?.displayCode}
                          >
                            {t('adminOrdersWorkspace.actions.openOrder', 'to Order')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        );
      })}
      <ConfirmAlertDialog
        open={pendingAction != null}
        onOpenChange={(open) => {
          if (!open) {
            closePendingAction();
          }
        }}
        title={pendingAction?.title ?? ''}
        description={pendingAction?.description}
        confirmText={pendingAction?.confirmText ?? t('adminOrdersWorkspace.confirmations.confirmButton', 'Confirm')}
        cancelText={t('adminOrdersWorkspace.confirmations.cancelButton', 'Cancel')}
        onConfirm={confirmPendingAction}
      />
      <ReuploadCommentDialog
        open={reuploadDialogItem != null}
        item={reuploadDialogItem}
        comment={reuploadComment}
        isSubmitting={reuploadSubmitting}
        onCommentChange={onReuploadCommentChange}
        onConfirm={onReuploadDialogConfirm}
        onOpenChange={onReuploadDialogOpenChange}
      />
    </>
  );
};

export default ItemGroupsSection;
